import { Box, ThemeProvider, CssBaseline, useMediaQuery } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import AppBar from './components/common/AppBar';
import SettingsModal from './components/Settings/SettingsModal';
import PlayerBar from './components/Player/PlayerBar';
import DesktopLayout from './components/Layout/DesktopLayout';
import MobileLayout from './components/Layout/MobileLayout';
import BottomNavigation from './components/common/BottomNavigation';
import AppDownloadBanner from './components/common/AppDownloadBanner';
import SharePlayModal from './components/Player/SharePlayModal';
import { useDownloadBanner } from './hooks/useDownloadBanner';
import { useFavoritesStore } from './stores/favoritesStore';
import { usePlayerStore } from './stores/playerStore';
import { useBandStore } from './stores/bandStore';
import { useSettingsStore } from './stores/settingsStore';
import { createFlutterTheme } from './theme/flutterTheme';
import { buildShareUrlPath } from './utils/shareUrl';

function App() {
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const loadFavorites = useFavoritesStore((state) => state.loadFavorites);
  const loadSharedSong = usePlayerStore((state) => state.loadSharedSong);
  const play = usePlayerStore((state) => state.play);
  const fetchMoreSongs = usePlayerStore((state) => state.fetchMoreSongs);
  const fetchBands = useBandStore((state) => state.fetchBands);

  const isLoadingFromShare = usePlayerStore((state) => state.isLoadingFromShare);
  const playbackBlocked = usePlayerStore((state) => state.playbackBlocked);
  const blockedSongInfo = usePlayerStore((state) => state.blockedSongInfo);

  // Show modal when: loading from share AND playback got blocked
  const showSharePlayModal = isLoadingFromShare && playbackBlocked;

  // Responsive breakpoints - switch to mobile view below 1128px
  const isMobile = useMediaQuery('(max-width:1127.95px)');

  const themeMode = useSettingsStore((state) => state.themeMode);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolvedMode = themeMode === 'system' ? (prefersDark ? 'dark' : 'light') : themeMode;
  const theme = useMemo(() => createFlutterTheme(resolvedMode), [resolvedMode]);

  const { showBanner, dismiss, storeUrl, storeName } = useDownloadBanner();
  const showDownloadBanner = isMobile && showBanner;

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Handle share URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('share') !== 'true') return;

    const collection = params.get('collection');
    const songId = params.get('songId');
    const showId = params.get('showId');
    const mode = params.get('mode') || 'normal';
    const source = params.get('source') || 'ls';

    if (!collection || !songId || !showId) return;

    // Only load the shared song if the player is idle (no active queue)
    const { queue } = usePlayerStore.getState();
    if (queue.length > 0) return;

    // Ensure bands are loaded before loading shared song
    const loadShare = async () => {
      await fetchBands();
      await loadSharedSong({ collection, songId, showId, mode, source });
    };
    loadShare();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync browser URL with currently playing song
  const currentItem = usePlayerStore(
    (state) => {
      const { currentIndex, queue } = state;
      return currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;
    },
  );
  const { fullShowMode, continueJamsMode } = useSettingsStore();

  useEffect(() => {
    if (currentItem) {
      const path = buildShareUrlPath({ item: currentItem, fullShowMode, continueJamsMode });
      window.history.replaceState({}, '', path);
    } else {
      // Don't clear params if we're loading a share or the URL still has share params
      const hasShareParams = new URLSearchParams(window.location.search).get('share') === 'true';
      if (!hasShareParams && !isLoadingFromShare) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [currentItem, fullShowMode, continueJamsMode, isLoadingFromShare]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}>
        <AppBar
          onSettingsClick={() => setSettingsModalOpen(true)}
          isMobile={isMobile}
        />

        {showDownloadBanner && (
          <AppDownloadBanner
            storeUrl={storeUrl}
            storeName={storeName}
            onDismiss={dismiss}
          />
        )}

        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          pt: showDownloadBanner ? '104px' : isMobile ? '56px' : '64px', // Adjust for AppBar height (+ download banner)
          pb: isMobile ? '112px' : '90px', // Space for PlayerBar + BottomNav on mobile
        }}>
          {isMobile ? (
            <MobileLayout activeTab={activeTab} />
          ) : (
            <DesktopLayout />
          )}
        </Box>

        {isMobile && (
          <BottomNavigation 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />
        )}

        <PlayerBar isMobile={isMobile} />

        <SettingsModal
          open={settingsModalOpen}
          onClose={() => setSettingsModalOpen(false)}
        />

        <SharePlayModal
          open={showSharePlayModal}
          songTitle={blockedSongInfo?.title || ''}
          artist={blockedSongInfo?.artist || ''}
          onPlay={() => {
            play();
            fetchMoreSongs(5);
          }}
          onCancel={() => {
            // Just close - user can play later from player bar
            usePlayerStore.getState().clearPlaybackBlocked();
          }}
        />
      </Box>
    </ThemeProvider>
  );
}

export default App;
